import type { DoseFrequency, DoseUnit, MedicationDosage, MedicationRoute } from "@repo/domain";

export const FREQUENCY_OPTIONS: { value: DoseFrequency; label: string; phrase: string }[] = [
  { value: "daily", label: "Once daily (QD)", phrase: "once daily" },
  { value: "twice-daily", label: "Twice daily (BID)", phrase: "twice daily" },
  { value: "three-times-daily", label: "Three times daily (TID)", phrase: "three times daily" },
  { value: "four-times-daily", label: "Four times daily (QID)", phrase: "four times daily" },
  { value: "every-4-hours", label: "Every 4 hours (Q4H)", phrase: "every 4 hours" },
  { value: "every-6-hours", label: "Every 6 hours (Q6H)", phrase: "every 6 hours" },
  { value: "every-8-hours", label: "Every 8 hours (Q8H)", phrase: "every 8 hours" },
  { value: "every-other-day", label: "Every other day (QOD)", phrase: "every other day" },
  { value: "at-bedtime", label: "At bedtime (QHS)", phrase: "at bedtime" },
  { value: "weekly", label: "Weekly", phrase: "once weekly" },
  { value: "monthly", label: "Monthly", phrase: "once monthly" },
];

export const ROUTE_OPTIONS: { value: MedicationRoute; label: string; phrase: string }[] = [
  { value: "oral", label: "Oral", phrase: "by mouth" },
  { value: "sublingual", label: "Sublingual", phrase: "under the tongue" },
  { value: "topical", label: "Topical", phrase: "topically" },
  { value: "transdermal", label: "Transdermal", phrase: "on the skin" },
  { value: "inhaled", label: "Inhaled", phrase: "inhaled" },
  { value: "nasal", label: "Nasal", phrase: "in the nose" },
  { value: "ophthalmic", label: "Eye", phrase: "in the eye" },
  { value: "otic", label: "Ear", phrase: "in the ear" },
  { value: "rectal", label: "Rectal", phrase: "rectally" },
  { value: "vaginal", label: "Vaginal", phrase: "vaginally" },
  { value: "subcutaneous", label: "Subcutaneous", phrase: "subcutaneously" },
  { value: "intramuscular", label: "Intramuscular", phrase: "intramuscularly" },
  { value: "intravenous", label: "Intravenous", phrase: "intravenously" },
];

export const UNIT_OPTIONS: { value: DoseUnit; label: string; plural: string }[] = [
  { value: "tablet", label: "tablet", plural: "tablets" },
  { value: "capsule", label: "capsule", plural: "capsules" },
  { value: "mg", label: "mg", plural: "mg" },
  { value: "mcg", label: "mcg", plural: "mcg" },
  { value: "g", label: "g", plural: "g" },
  { value: "mL", label: "mL", plural: "mL" },
  { value: "drop", label: "drop", plural: "drops" },
  { value: "puff", label: "puff", plural: "puffs" },
  { value: "patch", label: "patch", plural: "patches" },
  { value: "application", label: "application", plural: "applications" },
  { value: "unit", label: "unit", plural: "units" },
];

/** "1 tablet by mouth twice daily as needed". Undefined when nothing is structured. */
export function dosageSentence(dosage: MedicationDosage | undefined): string | undefined {
  if (!dosage) return undefined;
  const { amount, frequency, route, asNeeded } = dosage;
  const unit = amount && UNIT_OPTIONS.find((u) => u.value === amount.unit);
  const parts = [
    amount && unit ? `${amount.value} ${amount.value === 1 ? unit.label : unit.plural}` : undefined,
    route && ROUTE_OPTIONS.find((r) => r.value === route)?.phrase,
    frequency && FREQUENCY_OPTIONS.find((f) => f.value === frequency)?.phrase,
    asNeeded ? "as needed" : undefined,
  ].filter(Boolean);
  return parts.length ? parts.join(" ") : undefined;
}
